import Logger from "common/Logger"; const LOGGER = new Logger("areas.utils.ts");import http, { isHttpError } from "common/http"
import areasConfig from "components/project/areas/areas.config.json"
import { getNameAbbreviation } from "components/protest.utils.web"
import { UsersSchemaWithId } from "components/users/users.schema"
import { TreeContent } from "components/widgets/treeView/treeView"
import { DateTime } from "luxon"
import Vue from "vue"
import { Component } from "vue-property-decorator"

import { ProjectSchemaWithId } from "../project.schema"
import areasDb from "./areas.db"
import { AreasSchema, AreasSchemaWithId } from "./areas.schema"

/**
 * Create blank area
 *
 * @param requiredUserId user id
 * @param title
 * @param abbreviation
 * @param wbs_id
 * @returns ActivitiesSchema blank activity
 */
export function blankArea(title: string, abbreviation?: string, wbs_id?: string): AreasSchema {
    const currentTime = DateTime.utc().toISO()
    const data: AreasSchema = {
        created_by: -1,
        date_created: currentTime,
        date_modified: currentTime,
        modified_by: -1,
        data: {
            sub_areas: [],
            parent_area: -1,
            title,
            metadata: {
                wbs_id
            },
            abbreviation
        }
    }

    return data
}

/**
 * @param subAreaIds Array of ids
 * @param itemToPush TreeContent object
 * @param projectId project id
 */
export async function populateTree(subAreaIds: Array<string>, itemToPush: TreeContent, projectId: number): Promise<void> {
    const childAreas = await areasDb.readAll({
        filter: {
            id: subAreaIds
        }
    })

    if (isHttpError(childAreas)) {
        LOGGER.error("Failed to fetch areas from AreaDb", { error: childAreas })
        return
    }

    itemToPush.children = []
    for (const areaId of subAreaIds) {
        const childArea = childAreas.find((area) => area.id === +areaId)
        if (childArea) {
            const childItemToPush = {
                id: childArea.id.toString(),
                name: childArea.data.title,
                children: childArea.data.sub_areas.length > 0 ? [] : undefined,
                childrenId: childArea.data.sub_areas,
                parentId: itemToPush.id === "-1" ? undefined : itemToPush.id,
                wbsId: childArea.data.metadata.wbs_id
            }

            itemToPush.children.push(childItemToPush)
        }
    }
}

/**
 * get cached Path of area
 *
 * @param areaID Area id
 * @param areaPathCache area Path Cache
 * @returns area name
 */
export function getPathNameNoDb(areaID: number, areaPathCache?: Record<string, string>): string {
    if (areaID === -1) return ""
    if (areaPathCache) {
        const cachedPath = areaPathCache[areaID]
        if (cachedPath) return cachedPath
    }

    return ""
}

/**
 * @param areaID Area id to find string for
 * @param areaPathCache A key value object containing cached area path names
 * @param abbreviate boolean to check if user want to abbrivate or not
 * @param sessionID session id
 * @returns area hierarchy string
 */
export async function getPathName(areaID: number, areaPathCache?: Record<string, string>, abbreviate?: boolean, sessionID?: string): Promise<string> {
    if (areaID === -1) return ""
    if (areaPathCache) {
        const cachedPath = areaPathCache[areaID]
        if (cachedPath) return cachedPath
    }

    let areaPath = ""

    const parentAreas = await areasDb.readParents(areaID, "parent_area", sessionID)

    if (isHttpError(parentAreas)) {
        LOGGER.error("Failed to get area parents information.", { error: parentAreas })
        return ""
    }

    for (const area of parentAreas) {
        if (area.depth > 0) {
            if (abbreviate) areaPath = area.data.abbreviation ? area.data.abbreviation : getNameAbbreviation(area.data.title, 3) + " > " + areaPath
            else areaPath = area.data.title + " > " + areaPath
        } else {
            areaPath = area.data.title
        }
    }

    if (areaPathCache) areaPathCache[areaID] = areaPath

    return areaPath
}

/**
 * Returns id for given  area path
 *
 * @param areaPath Full path name of area
 * @param rootIds Root Ids of areas at root level
 * @param caseInsensitive
 * @returns number Area Id
 */
export async function getAreaCodeFromString(areaPath: string, rootIds: Array<number>, caseInsensitive = false): Promise<number> {
    let areaId = -1
    const delimiter = ">"

    const areaNames = areaPath.split(delimiter)
    const rootAreaName = areaNames[0].trim()

    const rootAreas = await areasDb.readAll({
        filter: {
            id: rootIds
        }
    })

    if (isHttpError(rootAreas)) return -1

    const rootArea = rootAreas.find((area) => {
        const trimmedAreaName = area.data.title.trim()
        if (caseInsensitive) return trimmedAreaName.toLowerCase() === rootAreaName.toLowerCase()
        else return trimmedAreaName === rootAreaName
    })

    if (rootArea) {
        if (areaNames.length === 1) {
            return rootArea.id
        } else {
            areaNames.splice(0, 1)
            if (rootArea.data.sub_areas.length > 0)
                areaId = await getAreaCodeFromString(areaNames.join(delimiter), rootArea.data.sub_areas, caseInsensitive)
            else return -1
        }
    }

    return areaId
}

/**
 * @param wbsId The wbs id.
 * @returns Promise<Array<number>>
 */
export async function getAreaFromWBS(wbsId: string): Promise<(AreasSchema & RequiredId)[]> {
    const area = await areasDb.readAll({
        filter: {
            "data.metadata.wbs_id": wbsId
        }
    })

    if (isHttpError(area)) {
        LOGGER.error("Failed to fetch areas from AreaDb", { error: area })
        return []
    }

    if (isHttpError(area)) return []
    else return area
}

/**
 * Check if an area is in use in any activity in current project
 *
 * @param areaId Area Id to check
 * @param projectId ProjectId to check in
 * @returns boolean is area in use
 */
export async function isAreaInUse(areaId: number, projectId: number): Promise<boolean> {
    const isInUse = await http.get<boolean>(`${process.env.FLYPAPER_API_URL}/areas/is/in/use/${areaId}/${projectId}`)

    if (isHttpError(isInUse)) return false

    return isInUse
}

/**
 * @param areaId The area id number.
 * @returns Promise<Array<number>>
 */
export async function getAncestors(areaId: number): Promise<Array<number>> {
    const parentIds: Array<number> = []
    const area = await areasDb.read(areaId)

    if (isHttpError(area) || area.length === 0) return []

    if (area[0].data.parent_area !== -1) parentIds.push(...(await getAncestors(area[0].data.parent_area)), area[0].data.parent_area)

    return parentIds
}

/**
 *
 * @param areaId
 */
export async function getExistingAbbreviation(areaId: number): Promise<string> {
    const area = await areasDb.read(areaId)
    if (isHttpError(area) || area.length === 0) return ""
    if (area[0].data.abbreviation) return area[0].data.abbreviation

    return ""
}

/**
 *
 * @param data
 * @param areas
 */
export function getCompleteAreaAbbreviation(data: string, areas?: AreasSchemaWithId[]) {
    if (!areas || !areas.length) return ""

    const areasTitleList = data.split(">")

    const areaPathAbbreviation = areasTitleList.map((areaName) => {
        const trimmedName = areaName.trim()
        const matchedArea = areas.find((area) => area.data.title === trimmedName)

        if (matchedArea?.data.abbreviation?.trim()) return matchedArea.data.abbreviation

        return getNameAbbreviation(matchedArea?.data.title || trimmedName, 3)
    })

    return areaPathAbbreviation.join(" > ")
}

/**
 * Get child area ids of a area
 *
 * @param areaId area Id for which children are to be get
 * @param sessionId the session id number
 * @returns Array<number> child area ids
 */
export async function getAllChildAreas(areaId: number, sessionId?: string | undefined): Promise<Array<number>> {
    const area = await areasDb.read(areaId, sessionId)

    if (isHttpError(area) || !area[0]) return []

    const subAreaIds = area[0].data.sub_areas
    if (!subAreaIds.length) return []
    // Fetched in parallel for speed, but reassembled depth-first (each child immediately followed by its own
    // descendants) to match getAllChildAreasNoDb - the two are used interchangeably to build area sequences.
    const childAreasPromises = subAreaIds.map((subAreaId) => getAllChildAreas(subAreaId, sessionId))
    const promiseResults = await Promise.all(childAreasPromises)
    const childIds: number[] = []
    subAreaIds.forEach((subAreaId, index) => {
        childIds.push(subAreaId)
        childIds.push(...promiseResults[index])
    })

    return childIds
}

/**
 * Synchronous equivalent of getAllChildAreas that reads from an already-loaded area list (e.g. the
 * currentProjectAreaDetails store getter) instead of issuing a DB read per node in the subtree - use this
 * whenever the caller already has the project's areas loaded, to avoid a recursive network fan-out.
 *
 * @param areaId area Id for which children are to be found
 * @param areaDetails already-loaded area list, each with its own childrenIds
 * @returns Array<number> child area ids
 */
export function getAllChildAreasNoDb(areaId: number, areaDetails: Array<{ id: number; childrenIds: number[] }>): number[] {
    const area = areaDetails.find((item) => item.id === areaId)
    if (!area || !area.childrenIds.length) return []

    const childIds: number[] = []
    for (const childId of area.childrenIds) {
        childIds.push(childId)
        childIds.push(...getAllChildAreasNoDb(childId, areaDetails))
    }

    return childIds
}

/**
 * Whether an area SSE changes which areas belong to the loaded project tree, rather than just an
 * existing area's fields. True for an unknown area hung under a loaded area (or at the root), or for a
 * loaded area whose sub_areas gained/lost ids - both mean the loaded area list must be refetched, since
 * patching the one record in place can't add the new child's record or drop a deleted one.
 *
 * @param loadedAreas the project's currently loaded areas
 * @param rootAreaIds the project's top-level area ids (project.data.areas)
 * @param incomingArea the area record pushed by the SSE
 * @returns true if the loaded areas must be refetched
 */
export function isAreaTreeMembershipChange(loadedAreas: AreasSchemaWithId[], rootAreaIds: number[], incomingArea: AreasSchemaWithId): boolean {
    const loadedArea = loadedAreas.find((area) => area.id === incomingArea.id)

    if (!loadedArea) return rootAreaIds.includes(incomingArea.id) || loadedAreas.some((area) => area.id === incomingArea.data.parent_area)

    const previousSubAreas = new Set(loadedArea.data.sub_areas)
    const nextSubAreas = new Set(incomingArea.data.sub_areas)

    return previousSubAreas.size !== nextSubAreas.size || [...nextSubAreas].some((id) => !previousSubAreas.has(id))
}

/**
 *
 * @param topLevelProjectAreas
 * @param sessionId
 */
export async function getProjectAreaSequence(topLevelProjectAreas: number[], sessionId?: string) {
    const allAreaIds = []
    for (const parentArea of topLevelProjectAreas) {
        allAreaIds.push(parentArea)
        const allAreas = await getAllChildAreas(parentArea, sessionId)
        for (const area of allAreas) allAreaIds.push(area)
    }

    return allAreaIds
}

@Component
export class AreasUtilsMixin extends Vue {
    public getMailSubjectLine(user: UsersSchemaWithId, project: ProjectSchemaWithId): string {
        return user.data.name + areasConfig.request_area.email_subject + project.data.name
    }
}
